import { LIGHT_GREY, NAVY, PROGRESS_LOW, PROGRESS_MID, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

type InsuranceType = 'loanProtectionInsurance' | 'unemploymentInsurance';

/** Anything carrying the Sale summary array (leads and clients both do). */
interface HasSales {
  sales?: Array<{
    insuranceType: InsuranceType;
    advancePercentage: number | null;
  }>;
}

/**
 * Gets the advance percentage for a specific insurance type from the entity's
 * matching Sale. Sale is the single source of truth — the legacy
 * `customAttributes.insurexData` fallback was removed.
 *
 * Works for both leads and clients since both expose a `sales` array.
 *
 * @param entity - The lead or client object
 * @param insuranceType - The insurance type ('loanProtectionInsurance' or 'unemploymentInsurance')
 * @returns The advance percentage or undefined if not found
 */
const getAdvancePercentage = (entity: HasSales, insuranceType: InsuranceType): number | undefined => {
  if (!Array.isArray(entity.sales)) return undefined;
  const sale = entity.sales.find((s) => s.insuranceType === insuranceType);
  return sale?.advancePercentage ?? undefined;
};

/**
 * Background for an advance-percentage chip (Mueve Seguro Desgravamen/Cesantia).
 * No-advance stays neutral grey; progress deepens along a single lightened-Navy
 * hue, landing on solid Navy near completion — on-brand with the design system
 * (no traffic-light colors). Pair with {@link getProgressTextColor}.
 */
export const getProgressColor = (percentage: number): string => {
  if (percentage >= 71 && percentage <= 100) return NAVY;
  if (percentage >= 31 && percentage <= 70) return PROGRESS_MID;
  if (percentage >= 1 && percentage <= 30) return PROGRESS_LOW;
  return LIGHT_GREY;
};

/** Foreground for the advance chip — flips to white once the Navy fill turns solid. */
export const getProgressTextColor = (percentage: number): string =>
  percentage >= 71 && percentage <= 100 ? SURFACE_CONTAINER_LOWEST : NAVY;

export default getAdvancePercentage;
